import { Card, Button, Row, Col, Badge } from "reactstrap";
import { Whisper, Tooltip } from "rsuite";
import AppSpinner from "../../elements/AppSpinner";
import { ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import { callFunctionIfExists } from "../../utils/GeneralUtils";

interface ItemCardListInterface {
  children?: any;
  link?: string;
  icon?: `mdi-${string}`;
  item: {
    Name?: string;
    Required?: boolean;
    Description?: string;
    FieldType?: string;
    Unique?: boolean;
    APIName?: string;
  };
  removable?: boolean;
  removeMessage?: string;
  removeAction?: any;
  removeStatus?: string;
  editable?: boolean;
  editableMessage?: string;
  editableAction?: (data: any) => void;
  summary?: boolean;
  redirectable?: boolean;
  /** `redirectable`: redirects to link.  `selectable`: highlights the item and executes selectAction */
  selectActionType?: "redirectable" | "selectable";
  selectAction?: any;
  isSelected?: boolean;
  hasError?: boolean;
  selectableMessage?: string;
  titleFontWeight?: number;
  borderedCards?: boolean;
  yMargin?: number;
  rightIcon?: string;
  leftCheckbox?: boolean;
  rightCustom?: ReactNode;
  nameLink?: string;
}

const ItemCardList = ({
  children,
  link = "null" /** Set the link where you want to redirect when the component is selectable */,
  icon = "mdi-file-table-box-outline" /** Set the Icon of the Card */,
  item /** Data for the Card*/,
  removable = false /** Enable remove the Field/Item */,
  removeMessage = "Remove" /** Remove button Tooltip */,
  removeAction /** Remove button Function */,
  removeStatus /** Status of the delition of the item for the spinner */,
  editable = false /** Enable edit option of the Field/Item */,
  editableMessage = "Edit" /** Edit button Tooltip */,
  editableAction /** Edit button Tooltip */,
  summary = false /** Enable to show a summary of the Field/Item */,
  selectActionType = undefined /** "redirectable": redirects to link.  "selectable": highlights the item and executes selectAction */,
  selectAction /** Function for action when clicked */,
  isSelected,
  hasError,
  selectableMessage = "Go To" /** Selectable Button Tooltip */,
  titleFontWeight = 500,
  borderedCards = false /**Card border */,
  yMargin = 0,
  rightIcon = undefined,
  leftCheckbox = false,
  rightCustom,
  nameLink,
}: ItemCardListInterface) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const handleClickItemCardList = () => {
    if (!selectActionType) return;

    if (selectActionType === "redirectable") {
      if (!link) return;
      return navigate(link);
    }

    if (selectActionType === "selectable") {
      selectAction();
    }
  };

  return (
    <Card
      onClick={() => handleClickItemCardList()}
      className={`d-flex justify-content-between shadow-none m-0 p-2 cursor-pointer border-none bg-dropdown-hover--hover my-${yMargin}`}
      style={{
        outline:
          isSelected && hasError
            ? "2px solid var(--danger)"
            : isSelected && !hasError
            ? "2px solid var(--dropdown-selected-dark)"
            : borderedCards
            ? "1px solid var(--light)"
            : "unset",
        background:
          isSelected && hasError
            ? "#ffa8a8"
            : isSelected && !hasError
            ? "var(--dropdown-selected)"
            : "unset",
      }}
    >
      <Row className="align-items-center justify-content-between m-0">
        {leftCheckbox && (
          <Col xs="auto" className="mr-4 ml-2 align-self-start">
            <i
              className={`mdi mdi-${
                isSelected ? "checkbox-marked" : "checkbox-blank-outline"
              }`}
              style={{
                fontSize: 20,
                color:
                  isSelected && hasError
                    ? "var(--danger)"
                    : isSelected && !hasError
                    ? "var(--primary)"
                    : "unset",
              }}
            />
          </Col>
        )}
        <Col xs="auto" className="pl-1 pr-3">
          {!summary ? (
            <>
              <i
                className={`mdi ${icon} mr-2 text-dark`}
                style={{ fontSize: 21 }}
              ></i>
            </>
          ) : (
            <>
              <Button
                onClick={() => setExpanded((prev) => !prev)}
                size="sm"
                color="secondary"
                className="btn-icon-only"
                outline
              >
                <i
                  className={`mdi ${
                    !expanded ? "mdi-chevron-right" : "mdi-chevron-down"
                  }`}
                ></i>
              </Button>
            </>
          )}
        </Col>
        <Col xs="" className="pl-0">
          <div>
            <h4
              style={{ width: "fit-content" }}
              className={`mb-0 text-dark font-weight-${titleFontWeight} ${
                nameLink
                  ? "cursor-pointer text-primary--hover text-underline--hover"
                  : ""
              }`}
              onClick={nameLink ? () => navigate(nameLink) : undefined}
            >
              {item.Name}
              {item.Required && <span className="text-danger">*</span>}
            </h4>
            {item.Description && (
              <small className="mb-2">{item.Description}</small>
            )}
          </div>
        </Col>
        {summary && (
          <Col>
            <p className="m-0 text-sm">
              {item.FieldType}
              {item.Unique && (
                <Badge
                  className="ml-2"
                  color="secondary"
                  style={{
                    position: "relative",
                    top: "-1px",
                  }}
                >
                  Unique
                </Badge>
              )}
            </p>
          </Col>
        )}
        <Col xs="auto" className="pr-1">
          <Row>
            {editable && (
              <>
                <Whisper
                  trigger="hover"
                  placement="autoHorizontal"
                  speaker={<Tooltip>{editableMessage}</Tooltip>}
                >
                  <Button
                    size="sm"
                    className={`btn-icon-only`}
                    outline
                    onClick={() => callFunctionIfExists(editableAction, item)}
                    color="secondary"
                  >
                    <i className="mdi mdi-pencil" />
                  </Button>
                </Whisper>
              </>
            )}
            {removable && (
              <>
                <Whisper
                  trigger="hover"
                  placement="autoHorizontal"
                  speaker={<Tooltip>{removeMessage}</Tooltip>}
                >
                  <Button
                    size="sm"
                    className={`btn-icon-only`}
                    outline
                    onClick={removeAction}
                    color={removable ? "danger" : ""}
                    disabled={removeStatus === item.APIName}
                  >
                    {removeStatus === item.APIName ? (
                      <AppSpinner color="danger" />
                    ) : (
                      <i className="mdi mdi-close" />
                    )}
                  </Button>
                </Whisper>
              </>
            )}
            {rightIcon && (
              <div className="text-right ml-4">
                <Whisper
                  trigger="hover"
                  placement="autoHorizontal"
                  speaker={<Tooltip>{selectableMessage}</Tooltip>}
                >
                  <Button
                    size="sm"
                    className={`btn-icon-only`}
                    outline
                    color="secondary"
                  >
                    <i className={rightIcon} />
                  </Button>
                </Whisper>
              </div>
            )}
            {rightCustom && (
              <div className="text-right ml-4">{rightCustom}</div>
            )}
          </Row>
        </Col>
      </Row>
      <Row className="align-items-center justify-content-between m-0">
        <div className="ml-5">{expanded && children}</div>
      </Row>
    </Card>
  );
};

export default ItemCardList;
